import React, { memo, useState, useEffect } from 'react';
import axios from 'axios';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  ZoomControl,
  useMap
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import API_BASE_URL from '../../config/api';
import { useTranslation } from '../../i18n';

const ElevationProfileCycling = ({ elevationData }) => {
  const { t } = useTranslation();
  if (!elevationData?.length) return null;

  const maxElevation = Math.max(...elevationData);
  const minElevation = Math.min(...elevationData);

  return (
    <div className="elevation-profile" style={{ marginTop: '20px' }}>
      <h3>{t("elevationProfile.title")}</h3>
      <p>Altura máxima: {maxElevation} m</p>
      <p>Altura mínima: {minElevation} m</p>
    </div>
  );
};

const customIcon = (iconUrl) =>
  new L.Icon({
    iconUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  });

const FitBoundsToRoute = ({ route }) => {
  const map = useMap();

  useEffect(() => {
    if (route?.length) {
      const bounds = L.latLngBounds(route);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, route]);

  return null;
};

const GeoLocationComponentCycling = memo(({ fileId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [routeData, setRouteData] = useState([]);
  const [elevationRecords, setElevationRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async (fileId) => {
    setLoading(true);
    try {
      console.log('📡 GeoLocationComponentCycling → fetchData iniciado con fileId:', fileId);
      const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
      const parsed = res.data;
      console.log('✅ Respuesta de la API:', parsed);
      setData(parsed);

      // Elevación
      const elevations = parsed.records
        ?.map((r) => r.enhanced_altitude || r.elevation)
        .filter((e) => typeof e === 'number' && !isNaN(e)) || [];

      setElevationRecords(elevations);
      console.log('📈 Elevaciones válidas encontradas:', elevations.length);

      // Ruta
      const coordinates = parsed.records
        ?.filter(r => typeof r.position_lat === 'number' && typeof r.position_long === 'number')
        .map(r => [r.position_lat, r.position_long]) || [];

      setRouteData(coordinates);
      console.log('📍 Ruta trazada con', coordinates.length, 'puntos.');

    } catch (err) {
      console.error('❌ Error al obtener los datos:', err);
      setError('Error al obtener los datos. Intenta nuevamente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fileId) fetchData(fileId);
  }, [fileId]);

  if (loading) return <div>{t('swimming.loading')}</div>;
  if (error) return <div>{error}</div>;
  if (!data?.session) return <div>{t("common.noData")}</div>;

  const {
    start_position_lat = 0,
    start_position_long = 0,
    swc_lat = 0,
    swc_long = 0,
    nec_lat = 0,
    nec_long = 0
  } = data.session;

  if (!start_position_lat || !start_position_long) {
    return <div>{t("social.geoLocation.noCoordinates")}</div>;
  }

  return (
    <div>
      <MapContainer
        center={[start_position_lat, start_position_long]}
        zoom={13}
        style={{ height: '400px', width: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {routeData.length > 0 && (
          <>
            <Polyline positions={routeData} color="blue" />
            <FitBoundsToRoute route={routeData} />
          </>
        )}

        <Marker position={[start_position_lat, start_position_long]}>
          <Popup>Ubicación inicial: {start_position_lat}, {start_position_long}</Popup>
        </Marker>

        <Marker position={[swc_lat, swc_long]} icon={customIcon('img/icons/start2.png')}>
          <Popup>{t('social.geoLocation.map.startPoint')}</Popup>
        </Marker>

        <Marker position={[nec_lat, nec_long]} icon={customIcon('img/icons/stop2.png')}>
          <Popup>{t('social.geoLocation.map.endPoint')}</Popup>
        </Marker>

        <ZoomControl position="topright" />
      </MapContainer>

    </div>
  );
});

export default GeoLocationComponentCycling;
